import { queryOptions, useMutation } from "@tanstack/react-query";
import sortBy from "lodash-es/sortBy";
import { DateTime } from "luxon";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import { traderSchema } from "@/api/types/digitalnisklady.cz/types";
import { entitySchema } from "@/common/schema/entity";
import { sortString } from "@/common/utils/array";
import { invariant } from "@/common/utils/invariant";
import { hasMatch, isMatchingSearch } from "@/common/utils/search";
import { getBestPrices } from "@/common/utils/trading";
import { graphql } from "@/generated/digitalnisklady.cz";
import {
  CropLastPricesQuery,
  Currency,
} from "@/generated/digitalnisklady.cz/graphql";

// eslint-disable-next-line lingui/no-unlocalized-strings
const ordersDocument = graphql(`
  query OrdersForOffer($id: ID!) {
    offer(id: $id) {
      orders {
        edges {
          node {
            active
            id
            amount
            currency
            quarter {
              id
              name
            }
            price
            lowPrice
            currency
          }
        }
      }
    }
  }
`);

const orderSchema = z.object({
  amount: z.number(),
  currency: z.enum(["CZK", "EUR"]),
  id: z.string(),
  lowPrice: z
    .number()
    .nullish()
    .transform((v) => v ?? null),
  price: z.number(),
  quarter: z.object({
    id: z.string(),
    name: z.string(),
  }),
});

type Order = z.infer<typeof orderSchema>;

const ordersQuery = ({ offerId }: { offerId: string }) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(ordersDocument, {
        id: offerId,
      }),
    queryKey: ["orders", offerId],
    select: (data) =>
      z
        .array(orderSchema)
        .parse(
          data.offer?.orders?.edges
            ?.filter((edge) => edge?.node?.price)
            .map((edge) => edge?.node),
        ),
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const addOrderDocument = graphql(`
  mutation AddOrder($order: OrderInput!) {
    addOrder(order: $order) {
      id
    }
  }
`);

const useAddOrderMutation = () =>
  useMutation({
    mutationFn: (payload: {
      offerId: string;
      quarterId: string;
      amount: number;
      lowPrice: number | null;
      price: number;
      currency: Currency;
      customTransport: boolean;
      transportNote?: string;
      note?: string;
      onlyTrader?: string;
    }) =>
      graphqlClient.request(addOrderDocument, {
        order: {
          active: true,
          amount: payload.amount,
          currency: payload.currency,
          customTransport: payload.customTransport,
          lowPrice: payload.lowPrice,
          note: payload.note,
          offer: payload.offerId,
          onlyTrader: payload.onlyTrader,
          price: payload.price,
          quarter: payload.quarterId,
          transportNote: payload.transportNote,
        },
      }),
    mutationKey: ["add-order"],
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const addContractDocument = graphql(`
  mutation AddContract($contract: ContractInput!) {
    addContract(contract: $contract) {
      id
    }
  }
`);

const useAddContractMutation = () =>
  useMutation({
    mutationFn: (payload: {
      offerId: string;
      marketPriceId: string;
      amount: number;
      note: string;
      currency: Currency;
      customTransport: boolean;
      transportNote?: string;
    }) =>
      graphqlClient.request(addContractDocument, {
        contract: {
          amount: payload.amount,
          currency: payload.currency,
          customTransport: payload.customTransport,
          marketPrice: payload.marketPriceId,
          note: payload.note,
          offer: payload.offerId,
          transportNote: payload.transportNote,
        },
      }),
    mutationKey: ["add-contract"],
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const cropLastPricesDocument = graphql(`
  query CropLastPrices(
    $id: ID!
    $currency: Currency!
    $cropIds: [ID!]
    $locale: String
  ) {
    storage(id: $id) {
      lastPrices(cropIds: $cropIds) {
        edges {
          node {
            id
            crop {
              id
              name(locale: $locale)
            }
            quarter {
              id
              name
            }
            trader {
              id
              name
              logo
              tocUrl
            }
            cropTrader {
              timeFromTrading
              timeToTrading
            }
            price(currency: $currency)
          }
        }
      }
    }
  }
`);

const cropLastPricesSchema = z.array(
  z.object({
    marketPriceId: z.string(),
    price: z.number(),
    quarter: entitySchema,
    trader: traderSchema,
  }),
);

const cropLastPricesQuery = ({
  currency,
  storageId,
  cropId,
  locale,
}: {
  currency: Currency;
  storageId: string;
  cropId: string;
  locale: string;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(cropLastPricesDocument, {
        cropIds: [cropId],
        currency: currency as Currency,
        id: storageId,
        locale,
      }),
    queryKey: ["last-prices", currency, storageId, cropId, locale],
  });

const sortLastPricesByQuarter = (
  data: CropLastPricesQuery,
): z.infer<typeof cropLastPricesSchema> => {
  const result = cropLastPricesSchema.parse(
    data.storage?.lastPrices?.edges?.map((edge) => ({
      marketPriceId: edge?.node?.id,
      price: edge?.node?.price,
      quarter: edge?.node?.quarter,
      trader: {
        ...edge?.node?.trader,
        timeFromTrading: edge?.node?.cropTrader?.timeFromTrading,
        timeToTrading: edge?.node?.cropTrader?.timeToTrading,
      },
    })),
  );

  return sortBy(result, (item) => sortQuarter(item.quarter.name));
};

// eslint-disable-next-line lingui/no-unlocalized-strings
const futureQuartersDocument = graphql(`
  query FutureQuarters {
    quarters(upcoming: true) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`);

const futureQuartersQuery = () =>
  queryOptions({
    queryFn: () => graphqlClient.request(futureQuartersDocument),
    queryKey: ["future-quarters"],
    select: (data) =>
      z
        .array(entitySchema)
        .parse(data.quarters?.edges?.map((edge) => edge?.node)),
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const cropAggregatedPricesDocument = graphql(`
  query StorageAggregatedPrices(
    $id: ID!
    $from: DateTime!
    $to: DateTime!
    $cropIds: [ID!]
    $currency: Currency!
  ) {
    storage(id: $id) {
      aggregatedPrices: prices(
        from: $from
        to: $to
        aggregated: true
        cropIds: $cropIds
      ) {
        edges {
          node {
            id
            date
            quarter {
              id
              name
              dateFrom
            }
            region {
              id
              name
            }
            price(currency: $currency)
            trader {
              id
              name
            }
          }
        }
      }
    }
  }
`);

const cropAggregatedPricesSchema = z.array(
  z.object({
    datetime: z.string(),
    quarter: z.object({
      name: z.string(),
    }),
    rate: z.number(),
  }),
);

const cropAggregatedPricesQuery = ({
  storageId,
  cropId,
  from,
  to,
  currency,
  traderId,
}: {
  traderId: string;
  storageId: string;
  cropId: string;
  from: DateTime;
  to: DateTime;
  currency: Currency;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(cropAggregatedPricesDocument, {
        cropIds: [cropId],
        currency: currency as Currency,
        from: from.toSeconds(),
        id: storageId,
        to: to.toSeconds(),
      }),
    queryKey: ["crop", "price-chart", storageId, cropId, from, to, currency],
    select: (data) => {
      const result = data.storage?.aggregatedPrices?.edges
        ?.filter((edge) => edge?.node?.trader?.id === traderId)
        .map((edge) => ({
          datetime: DateTime.fromSeconds(edge?.node?.date).toLocaleString(
            DateTime.DATE_SHORT,
          ),
          quarter: {
            name: edge?.node?.quarter?.name?.replace(" ", "/"),
          },
          rate: edge?.node?.price,
        }));
      return cropAggregatedPricesSchema.parse(result);
    },
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const storagesQueryDocument = graphql(`
  query StoragesForTrading(
    $companyId: ID!
    $currency: Currency!
    $archived: Boolean
    $locale: String
  ) {
    company(id: $companyId) {
      storages(archived: $archived, tradeType: FROM_STORAGE) {
        edges {
          node {
            archived
            id
            label
            offers {
              edges {
                node {
                  yearHarvested
                  id
                  archived
                  amount
                  totalAmount
                  crop {
                    id
                    name(locale: $locale)
                  }
                }
              }
            }
            lastPrices {
              edges {
                node {
                  price(currency: $currency)
                  crop {
                    id
                    name(locale: $locale)
                  }
                  quarter {
                    id
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

const offerSchema = z.object({
  amount: z.number(),
  crop: entitySchema.extend(z.object({ mark: z.boolean() }).shape),
  harvestYear: z.number(),
  id: z.string(),
  prices: z.array(
    z.object({
      currency: z.string(),
      id: z.string(),
      price: z.number(),
      quarter: entitySchema,
      value: z.number(),
    }),
  ),
});

const storagesSchema = z.array(
  entitySchema.extend(
    z.object({
      mark: z.boolean(),
      offers: z.array(offerSchema),
    }).shape,
  ),
);

const storagesQuery = ({
  companyId,
  currency,
  search,
  locale,
}: {
  currency: "CZK" | "EUR";
  companyId: string;
  search?: string;
  locale: string;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(storagesQueryDocument, {
        archived: false,
        companyId,
        currency: currency as Currency,
        locale,
      }),
    queryKey: ["storages", companyId, currency, locale],
    select: (data) => {
      const storages =
        data.company?.storages?.edges
          ?.filter((storage) => !storage?.node?.archived)
          .map((storage) => {
            return {
              id: storage?.node?.id,
              mark: hasMatch(storage?.node?.label, search),
              name: storage?.node?.label,
              offers: storage?.node?.offers?.edges
                ?.filter((offer) => !offer?.node?.archived)
                .filter((offer) => offer?.node?.yearHarvested) // this way we know it's a harvest being sold, not material being bought
                .reduce<z.infer<typeof offerSchema>[]>((allOffers, offer) => {
                  const currentOffer = offer?.node;
                  let index = allOffers.findIndex(
                    (o) => o.id === currentOffer?.id,
                  );

                  if (index === -1) {
                    index =
                      allOffers.push({
                        amount: currentOffer!.amount!,
                        crop: {
                          id: currentOffer!.crop!.id!,
                          mark: hasMatch(currentOffer?.crop?.name, search),
                          name: currentOffer!.crop!.name!,
                        },
                        harvestYear: currentOffer!.yearHarvested!,
                        id: currentOffer!.id!,
                        prices: [],
                      }) - 1;
                  }

                  const prices =
                    storage.node?.lastPrices?.edges?.filter(
                      (price) =>
                        price?.node?.crop?.id === offer?.node?.crop?.id,
                    ) ?? [];

                  // @ts-expect-error checked with zod schema
                  allOffers[index].prices = prices.map((price) => ({
                    currency,
                    id: price!.node!.quarter!.id,
                    price: price!.node!.price,
                    quarter: price!.node!.quarter,
                    value: price!.node!.price,
                  }));

                  allOffers[index].prices = getBestPrices(
                    allOffers[index].prices,
                  );

                  return allOffers;
                }, []),
            };
          }) ?? [];

      const safeStorages = storagesSchema
        .parse(storages)
        .filter((storage) => isMatchingSearch(storage, search))
        .toSorted(sortString);

      safeStorages.forEach((storage) => {
        storage.offers = sortBy(storage.offers, (o) => [
          o.crop.name,
          o.harvestYear,
        ]).map(({ prices, ...offer }) => {
          return {
            ...offer,
            prices: sortBy(prices, (p) => sortQuarter(p.quarter.name)),
          };
        });
      });

      return safeStorages;
    },
  });

const sortQuarter = (name: string) => {
  const [quarter, year] = name.split(" ");
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(quarter, "quarter extraction failed");
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(year, "year extraction failed");

  return [year, quarter];
};

export {
  cropAggregatedPricesQuery,
  cropLastPricesQuery,
  futureQuartersQuery,
  ordersQuery,
  sortLastPricesByQuarter,
  storagesQuery,
  useAddContractMutation,
  useAddOrderMutation,
};
export type { Order };
